import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterOption, SelectFilterField } from '@/components/common/AdvancedFilter';
import { useDebounce } from '@/hooks/useDebounce';
import { userListQueryOptions } from '@/features/users/api/users.api';
import { USER_TYPE } from '@/features/users/constants/user.constants';
import type { UserDetail, UserListItem } from '@/features/users/types/user.types';
import { getPersonOptionDetail } from '@/utils/personOption';

const MAPPING_USER_TYPE_IDS = [USER_TYPE.TSO, USER_TYPE.AGENT] as const;
const MAPPING_USER_LIMIT = 50;

function toMappingUserOptions(users: UserListItem[] | undefined): FilterOption[] {
  return (users ?? []).map((user) => ({
    value: String(user.id),
    label: user.fullName,
    description: getPersonOptionDetail(user),
  }));
}

export function useMappingUserFilterField(
  selectedId: string,
  selectedUser: UserDetail | undefined,
): SelectFilterField {
  const [query, setQuery] = useState('');
  const searchText = useDebounce(query.trim());

  const { data, isFetching } = useQuery(
    userListQueryOptions({
      userTypeId: MAPPING_USER_TYPE_IDS,
      status: 'active',
      search: searchText || undefined,
      limit: MAPPING_USER_LIMIT,
    }),
  );

  const results = toMappingUserOptions(data?.data);
  const isSelectedInResults =
    selectedId === '' || results.some((option) => option.value === selectedId);
  const selectedLabel = selectedUser?.fullName ?? `User #${selectedId}`;
  const options = isSelectedInResults
    ? results
    : [{ value: selectedId, label: selectedLabel }, ...results];

  return {
    key: 'userId',
    label: 'ZM Name',
    type: 'select',
    placeholder: 'Pick a ZM',
    options,
    onSearchChange: setQuery,
    isSearching: isFetching,
    isClearable: false,
    isRequired: true,
  };
}
